/**
 * Centralized error handling utilities for TanStack Query
 */

export interface AppError extends Error {
  status?: number;
  code?: string;
  details?: any;
}

export function createAppError(message: string, status?: number, code?: string, details?: any): AppError {
  const error = new Error(message) as AppError;
  error.status = status;
  error.code = code;
  error.details = details;
  return error;
}

/**
 * postgrest-js (1.16, shipped via the lockfile) returns `error` as JSON.parse(body): a plain
 * object, not an Error. Thrown as-is, every `instanceof Error` consumer (toasts,
 * isReconnectError, GlitchTip grouping) loses the message. Wrap it, keeping code/details/hint.
 */
export function toError<E extends { message: string }>(error: E): Error & E {
  return error instanceof Error ? error : Object.assign(new Error(error.message), error);
}

/**
 * True when the error is a PostgreSQL unique-constraint violation (23505).
 * Used to turn cryptic "duplicate key" DB errors into actionable messages.
 */
export function isUniqueViolation(error: any): boolean {
  return error?.code === '23505';
}

export function isRetryableError(error: any): boolean {
  // Network errors are retryable
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return true;
  }
  
  // Rate limit errors are retryable
  if (error?.status === 429) {
    return true;
  }
  
  // Some server errors are retryable
  if (error?.status >= 500 && error?.status < 600) {
    return true;
  }
  
  // Specific error types we know are temporary
  if (error?.message?.includes('connection') || 
      error?.message?.includes('timeout') ||
      error?.message?.includes('network')) {
    return true;
  }
  
  // Known permanent errors to avoid retrying
  const PERMANENT_ERROR_CODES = [
    '42702', // Column reference "value" is ambiguous
    '42703', // Column does not exist
    'PGRST100', // PostgREST syntax error
    '42P01', // Table does not exist
    '42883', // Function does not exist
    '42601', // Syntax error
    '23505', // Unique constraint violation
    '23503', // Foreign key constraint violation
    '42501', // Insufficient privilege
  ];
  
  if (error?.code && PERMANENT_ERROR_CODES.includes(error.code)) {
    return false;
  }
  
  // Don't retry on authentication errors
  if (error?.message?.includes('auth') || 
      error?.status === 401 || 
      error?.status === 403) {
    return false;
  }
  
  // Don't retry on client errors (400-499) except rate limiting
  if (error?.status >= 400 && error?.status < 500 && error?.status !== 429) {
    return false;
  }
  
  // Default to not retrying
  return false;
}

export function getRetryDelay(attemptIndex: number): number {
  // Exponential backoff with jitter
  const baseDelay = Math.min(1000 * Math.pow(2, attemptIndex), 10000);
  const jitter = Math.random() * 1000;
  return baseDelay + jitter;
}

export function getErrorMessage(error: any): string {
  // Handle Supabase errors
  if (error?.message) {
    // Make PostgreSQL errors more user-friendly
    if (error.code === '23505') {
      return 'This record already exists. Please check for duplicates.';
    }
    if (error.code === '23503') {
      return 'Cannot delete this record because it is referenced by other data.';
    }
    if (error.code === '42501') {
      return 'You do not have permission to perform this action.';
    }
    if (error.message.includes('JWT expired')) {
      return 'Your session has expired. Please sign in again.';
    }
    if (error.message.includes('Row Level Security')) {
      return 'You do not have access to this data.';
    }
    
    return error.message;
  }
  
  // Handle network errors
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return 'Network error. Please check your internet connection.';
  }
  
  // Handle rate limiting
  if (error?.status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  
  // Handle server errors
  if (error?.status >= 500) {
    return 'Server error. Please try again later.';
  }
  
  // Handle authentication errors
  if (error?.status === 401) {
    return 'Authentication required. Please sign in.';
  }
  
  if (error?.status === 403) {
    return 'Access denied. You do not have permission for this action.';
  }
  
  // Handle other client errors
  if (error?.status >= 400 && error?.status < 500) {
    return 'Invalid request. Please check your input and try again.';
  }
  
  // Fallback
  return 'An unexpected error occurred. Please try again.';
}

export function shouldShowErrorToast(error: any): boolean {
  // Don't show toast for authentication errors - these should be handled by auth flow
  if (error?.status === 401 || error?.message?.includes('JWT expired')) {
    return false;
  }
  
  // Don't show toast for network errors during background refetches
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return false;
  }
  
  // Show toast for all other errors
  return true;
}

export const errorHandling = {
  isRetryableError,
  getRetryDelay,
  getErrorMessage,
  shouldShowErrorToast,
  createAppError,
} as const;

export default errorHandling;